import { createContext, useContext, useState, type ReactNode } from "react";
import type {
  WidgetDesktopPosition,
  WidgetConfig,
  WidgetSize,
} from "@/types/widget";

export type WidgetDragSource = "sidebar" | "desktop" | "store";

export type DraggedItem = {
  id: string;
  type: "widget" | "icon";
  size: WidgetSize | "icon";
  source?: WidgetDragSource;
  desktopPosition?: WidgetDesktopPosition;
  config?: WidgetConfig;
};

// Metadata: Stable during a drag operation
interface WidgetDragMetadataContextType {
  activeWidget: WidgetConfig | null;
  setActiveWidget: (widget: WidgetConfig | null) => void;
  activeItem: DraggedItem | null;
  setActiveItem: (item: DraggedItem | null) => void;
}

// Movement: Volatile, updates on every mouse move
interface WidgetDragMovementContextType {
  currentDesktopDropPosition: WidgetDesktopPosition | null;
  setCurrentDesktopDropPosition: (
    position: WidgetDesktopPosition | null
  ) => void;
  currentSidebarDropIndex: number | null;
  setCurrentSidebarDropIndex: (index: number | null) => void;
}

const WidgetDragMetadataContext =
  createContext<WidgetDragMetadataContextType | null>(null);
const WidgetDragMovementContext =
  createContext<WidgetDragMovementContextType | null>(null);

export function WidgetDragProvider({ children }: { children: ReactNode }) {
  const [activeWidget, setActiveWidget] = useState<WidgetConfig | null>(null);
  const [activeItem, setActiveItem] = useState<DraggedItem | null>(null);
  const [currentDesktopDropPosition, setCurrentDesktopDropPosition] =
    useState<WidgetDesktopPosition | null>(null);
  const [currentSidebarDropIndex, setCurrentSidebarDropIndex] = useState<
    number | null
  >(null);

  return (
    <WidgetDragMetadataContext.Provider
      value={{
        activeWidget,
        setActiveWidget,
        activeItem,
        setActiveItem,
      }}
    >
      <WidgetDragMovementContext.Provider
        value={{
          currentDesktopDropPosition,
          setCurrentDesktopDropPosition,
          currentSidebarDropIndex,
          setCurrentSidebarDropIndex,
        }}
      >
        {children}
      </WidgetDragMovementContext.Provider>
    </WidgetDragMetadataContext.Provider>
  );
}

export function useWidgetDragMetadata() {
  const context = useContext(WidgetDragMetadataContext);
  if (!context) {
    throw new Error(
      "useWidgetDragMetadata must be used within WidgetDragProvider"
    );
  }
  return context;
}

export function useWidgetDragMovement() {
  const context = useContext(WidgetDragMovementContext);
  if (!context) {
    throw new Error(
      "useWidgetDragMovement must be used within WidgetDragProvider"
    );
  }
  return context;
}
